/* @flow */
import React from 'react'
import { Link } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import ErrorService from '@/services/errors/ErrorService'
import { feedUrl, feedbackUrl } from '@/urls'
import { i18n } from '@/locale/i18n'
import './ErrorHandler.scss'
import Fishbowl from '@/components/fish-bowl'

function NotificationErrorFallback() {
  return (
    <div className="feedError">
      <div className="feedError__container">
        <div className="feedError__tooltip">
          <div className="feedError__tooltip__image"></div>
          <i className="tooltip__arrow"></i>
        </div>
        <p
          className="view-how-to-buy-bitcoin__intro"
          dangerouslySetInnerHTML={{ __html: i18n('error_messages.feed_page_crash') }}
        />
      </div>
    </div>
  )
}

function DefaultErrorFallback({ error, resetErrorBoundary }) {
  const [showFullError, setShowFullError] = React.useState(false)
  return (
    <div
      className={`error-handler${showFullError && error ? ' error-handler__bigger' : ''} flex flex-col items-center justify-center min-h-[60vh] bg-white p-6 rounded shadow-md`}>
      <div className="error-handler__message flex flex-col items-center">
        {/* <Fishbowl /> */}
        <span className="text-2xl font-bold mt-4">{i18n('common.oops')}</span>
        <p className="mt-2 text-center" dangerouslySetInnerHTML={{ __html: i18n('error.text') }} />
        <Link className="button button--error mt-4" to={feedUrl()} onClick={resetErrorBoundary}>
          {i18n('error.button')}
        </Link>
        <div className="error-handler__more-option mt-2 text-sm">
          <Link to={feedbackUrl('bug-report')}>report this bug</Link> &nbsp; or &nbsp;
          <span onClick={() => setShowFullError(true)} className="link cursor-pointer text-blue-600 underline">
            See full error
          </span>
        </div>
        {error && showFullError && (
          <div className="error-handler__full_error mt-4 p-2 bg-gray-100 rounded text-xs text-left whitespace-pre-wrap">
            {error.message}
            {error.stack && <pre>{error.stack}</pre>}
          </div>
        )}
      </div>
    </div>
  )
}

function ErrorHandler({ children, component, fallback }) {
  const onError = (error, info) => {
    // Check if error contains "Minified React error" and refresh if so
    const errorMessage = error?.message || ''
    const errorStack = error?.stack || ''
    const errorString = `${errorMessage} ${errorStack}`.toLowerCase()

    if (errorString.includes('minified react error')) {
      window.location.reload()
      return
    }

    // Prefer an explicit prop. From componentStack, skip production-mangled
    // names (1–2 letters like "O"/"D") — those are Vite locals, not React components.
    let resolvedComponent = component
    if (!resolvedComponent && info?.componentStack) {
      const matches = [...info.componentStack.matchAll(/^\s*at ([A-Za-z_$][\w$]*)/gm)]
      const useful = matches.map(m => m[1]).find(name => name && name.length > 2)
      if (useful) resolvedComponent = useful
    }

    // Report error using centralized ErrorService
    ErrorService.captureError(error, {
      component: resolvedComponent || 'unknown',
      type: 'react_error',
      currentUrl: location.pathname,
      componentStack: info?.componentStack,
    })
  }
  return (
    <ErrorBoundary
      onError={onError}
      fallbackRender={({ error, resetErrorBoundary }) =>
        fallback ? (
          typeof fallback === 'function' ? (
            fallback({ error, resetErrorBoundary })
          ) : (
            fallback
          )
        ) : component === 'notification-widget' ? (
          <NotificationErrorFallback />
        ) : (
          <DefaultErrorFallback error={error} resetErrorBoundary={resetErrorBoundary} />
        )
      }>
      {children}
    </ErrorBoundary>
  )
}

export default ErrorHandler
